import invariant from 'invariant';
import { useCallback, useEffect } from 'react';

import { apiOpenWebOauthHandshake } from 'api/openweb/apiOpenWebOauthHandshake';

import { useCommentsStore } from './commentsStore';
import { loadOpenWebScriptOnce } from './loadOpenWebScriptOnce';
import {
  clearOpenWebLocalStorage,
  setOpenWebIdealistUserIdLocalStorageValue,
  shouldClearOpenWebLocalStorageForUserId,
} from './openWebLocalStorage';

type Args = {
  spotId: string;
  userId: string | undefined;
  hasPublicProfile: boolean;
};

export function useLoadOpenWeb({ spotId, userId, hasPublicProfile }: Args) {
  const { onReady } = useCommentsStore();

  const startSSO = useCallback(() => {
    invariant(window.SPOTIM, 'Attempting to startSSO without SPOTIM available');
    invariant(userId, 'Attempting to startSSO without userId');

    window.SPOTIM.startSSO({
      callback: async (codeA, completeSSOCallback) => {
        const codeB = await apiOpenWebOauthHandshake({ codeA, spotId });
        completeSSOCallback(codeB);

        setOpenWebIdealistUserIdLocalStorageValue(userId);
      },
    });
  }, [spotId, userId]);

  const load = useCallback(async () => {
    if (!hasPublicProfile || shouldClearOpenWebLocalStorageForUserId(userId)) {
      // We need to clear OpenWeb's local storage keys before loading the script,
      // otherwise it will use the previous user
      clearOpenWebLocalStorage();
    }

    await loadOpenWebScriptOnce({ spotId });
    // NOTE: a better approach would be to wait for a conversation, using
    // the spot-im-conversation-loaded event.
    // However, this event doesn't seem to be consistently fired.
    // https://developers.openweb.com/docs/event-listener-reference#initial-loading
    onReady();
    // eslint-disable-next-line no-underscore-dangle
    window.__OPEN_WEB__?.initOW();

    if (userId && hasPublicProfile) startSSO();
  }, [hasPublicProfile, userId, spotId, startSSO, onReady]);

  useEffect(() => {
    document.addEventListener('spot-im-renew-sso', startSSO, false);
    load();

    return () => {
      document.removeEventListener('spot-im-renew-sso', startSSO);
    };
  }, [load, startSSO]);
}
